var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { z } from 'zod';
import { TransportError } from './framework/Transport';
const Response = z.object({
    message: z.string(),
});
const statusResponse = z.object({
    firmwareVersion: z.string(),
    boardRevision: z.number(),
    temperature: z.number(),
    networkEth0: z.object({
        mode: z.string(),
        mac: z.string(),
        ip: z.string(),
        netmask: z.string(),
        gateway: z.string(),
    }),
    hdmiOutput: z.string(),
    usbOutput: z.string(),
    videoInputSwitchMode: z.string(),
    videoInputs: z.array(z.object({
        name: z.string(),
        port: z.string(),
        vid: z.number().optional(),
        pid: z.number().optional(),
        index: z.number(),
        isSelected: z.boolean(),
        selectedFormat: z.string(),
    })),
    audioInputSwitchMode: z.string(),
    audioInputs: z.array(z.object({
        input: z.string().optional(),
    })),
});
function isResp(schema, resp) {
    return schema.safeParse(resp).success;
}
export class InogeniCam230 {
    constructor(http, state, log, framework) {
        this.http = http;
        this.state = state;
        // other class properties and constructor
        this.authCredentials = '';
        // Mapping between the codes used to select a source and the returned codes.
        this.sourceNames = {
            1: 'usb1',
            2: 'usb2',
            3: 'hdmi',
            4: 'testpattern',
            usb1: '1',
            usb2: '2',
            hdmi: '3',
            testpattern: '4',
        };
        // controls
        this.busy = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                return this.state.busy.value;
            }),
        };
        this.firmwareVersion = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                return this.state.firmwareVersion.value;
            }),
        };
        this.videoMatrixRoutes = {
            get: (key, tags) => __awaiter(this, void 0, void 0, function* () {
                const resp = yield this.httpGetJson({ cmd: `/api/v1/status` }, statusResponse, tags);
                const videoInputs = resp.videoInputs;
                for (const videoInput of videoInputs) {
                    if (videoInput.isSelected) {
                        const srcName = videoInput.port.toLowerCase();
                        if (srcName in this.sourceNames) {
                            return this.sourceNames[srcName];
                        }
                    }
                }
                return this.state.videoMatrixRoutes.values[key];
            }),
            set: (key, value, tags) => __awaiter(this, void 0, void 0, function* () {
                const resp = yield this.httpPostJson({ cmd: `/api/v1/setVideoInput?input=${value}` }, Response, tags);
                return resp.message === 'success' ? value : this.state.videoMatrixRoutes.values[key];
            }),
        };
        // polling
        this.polls = [
            {
                intervalMs: 3000,
                controls: ['videoMatrixRoutes'],
            },
        ];
    }
    // phase callbacks
    onConfigure(config) {
        return __awaiter(this, void 0, void 0, function* () {
            const resp = yield this.httpGetJson({ cmd: `/api/v1/status` }, statusResponse, [
                'initialize',
            ]);
            this.state.firmwareVersion.update(resp.firmwareVersion);
        });
    }
    onInitialize(credentials) {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            const username = (_a = credentials.username) !== null && _a !== void 0 ? _a : 'admin';
            const password = (_b = credentials.password) !== null && _b !== void 0 ? _b : 'admin';
            this.authCredentials = btoa(`${username}:${password}`);
            try {
                const resp = yield this.httpGetJson({ cmd: `/api/v1/status` }, statusResponse, [
                    'initialize',
                ]);
                this.state.firmwareVersion.update(resp.firmwareVersion);
                return 'connected';
            }
            catch (error) {
                if (!(error instanceof TransportError && error.code === 'sendTimeout')) {
                    throw error;
                }
            }
            return 'verifyFailed';
        });
    }
    // other class methods
    httpGetJson(cmd, schema, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            const requestOptions = {};
            if ('data' in cmd)
                requestOptions.data = cmd.data;
            requestOptions.headers = { Authorization: `Basic ${this.authCredentials}` };
            requestOptions.tags = tags;
            const resp = yield this.http.requestJson('get', cmd.cmd, requestOptions);
            if (!isResp(schema, resp)) {
                throw new Error(`unexpected response: ${JSON.stringify(resp)}`);
            }
            return resp;
        });
    }
    httpPostJson(cmd, schema, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            const requestOptions = {};
            if ('data' in cmd)
                requestOptions.data = cmd.data;
            requestOptions.headers = { Authorization: `Basic ${this.authCredentials}` };
            requestOptions.tags = tags;
            const resp = yield this.http.requestJson('post', cmd.cmd, requestOptions);
            if (!isResp(schema, resp)) {
                throw new Error(`unexpected response: ${JSON.stringify(resp)}`);
            }
            return resp;
        });
    }
}
// static properties
InogeniCam230.driverType = 'httpDriver';
InogeniCam230.key = '-NwVDmsic-i9xVvxQPcm';
InogeniCam230.httpRequestOptions = {
    scheme: 'https',
};
